import React, { useCallback, useEffect, useState } from "react";
import useAmsData from "./hooks/useAmsData.js";
import useActionRunner from "./hooks/useActionRunner.js";
import Header, { ACTIONS } from "./components/Header.js";
import { Banner, ProblemAlert, Tabs, TABS } from "./components/Notices.js";
import DataTab from "./components/DataTab.js";
import StoresTab from "./components/StoresTab.js";
import SubscriptionsTab from "./components/SubscriptionsTab.js";
import { fmt, todayUtc } from "./components/ui.js";

/** Tab from the URL hash (#data, #stores, #subscriptions), so a link can open a tab directly. */
function useHashTab() {
  const read = () => (TABS.includes(window.location.hash.slice(1)) ? window.location.hash.slice(1) : "data");
  const [tab, setTab] = useState(read);
  useEffect(() => {
    const onHash = () => setTab(read());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const go = useCallback((name) => {
    if (window.location.hash !== `#${name}`) window.location.hash = name;
    else setTab(name);
  }, []);
  return [tab, go];
}

const labelOf = (action) => (ACTIONS.find((a) => a.key === action) || {}).label || action;

function describeResult(action, r) {
  if (!r) return `${labelOf(action)} finished.`;
  const errors = r.errors && r.errors.length ? `, ${r.errors.length} errors` : "";
  if (action === "sync-statuses") {
    return `Statuses synced: ${fmt(r.updated)} subscriptions updated across ${fmt(r.profiles)} profiles${errors}.`;
  }
  const ds = r.dataset === "sp-traffic" ? "traffic" : "conversion";
  const synced = r.statuses_updated != null ? `, ${fmt(r.statuses_updated)} statuses updated` : "";
  return `Confirmed ${fmt(r.confirmed)} pending ${ds} subscriptions (${fmt(r.scanned)} messages scanned)${synced}${errors}.`;
}

export default function MarketingStreamPage() {
  const [date, setDate] = useState(todayUtc);
  const [tab, goTab] = useHashTab();
  const [storeFilter, setStoreFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const [message, setMessage] = useState(null);
  const { data, loading, error, reload } = useAmsData(date);

  const onFinish = useCallback((action, out) => {
    if (out.ok) {
      setMessage({ text: describeResult(action, out.result) });
      reload(true);
    } else if (out.stillRunning) {
      setMessage({ text: `${labelOf(action)} is still running. Check back in a few minutes.`, error: true });
    } else {
      setMessage({ text: `${labelOf(action)} ${out.notStarted ? "could not start" : "failed"}: ${out.error}.`, error: true });
    }
  }, [reload]);

  const { busy, run } = useActionRunner(data && data.actions.actions, onFinish);

  const onAction = async (action) => {
    const { alreadyRunning } = await run(action);
    if (alreadyRunning) setMessage({ text: `${labelOf(action)} is already running. Showing its progress.` });
  };

  const showProblems = (name) => {
    if (name === "stores") setStoreFilter("red");
    else setSubFilter("red");
    goTab(name);
  };

  const banner = error && !data
    ? { text: `Could not load Marketing Stream data: ${error.message}. Refresh the page; if it keeps failing, check that you are logged in.`, error: true }
    : message;

  return (
    <div className={`ams${loading && data ? " ams--loading" : ""}`}>
      <Header generatedAt={data && data.overview.generated_at} date={date} onDateChange={setDate} onReload={reload}
        busy={busy} onAction={onAction} actions={data && data.actions.actions} loading={loading} />

      <Banner message={banner} onClose={() => setMessage(null)} />
      {data && <ProblemAlert summary={data.overview.summary} onShow={showProblems} />}

      <Tabs tab={tab} onChange={goTab}
        storeCounts={data && data.stores.counts} subCounts={data && data.subscriptions.counts} />

      {!data ? (
        <div className="ams-empty">{error ? "No data to show." : "Loading Marketing Stream data…"}</div>
      ) : (
        <>
          <div role="tabpanel" id="ams-panel-data" aria-labelledby="ams-tab-data" hidden={tab !== "data"}>
            <DataTab overview={data.overview} />
          </div>
          <div role="tabpanel" id="ams-panel-stores" aria-labelledby="ams-tab-stores" hidden={tab !== "stores"}>
            <StoresTab stores={data.stores.stores} filter={storeFilter} onFilter={setStoreFilter} />
          </div>
          <div role="tabpanel" id="ams-panel-subscriptions" aria-labelledby="ams-tab-subscriptions" hidden={tab !== "subscriptions"}>
            <SubscriptionsTab subscriptions={data.subscriptions.subscriptions} filter={subFilter} onFilter={setSubFilter} />
          </div>
        </>
      )}
    </div>
  );
}
